/**
 * Shared derived-data utilities for the NiV admin dashboard.
 *
 * Philosophy: manual CRM data (niv_clinic_crm) is a thin layer connected to
 * the clinic's real data — everything here derives display state from real
 * fields (plan, trialEndDate, planEndDate, updatedAt, isSoloClinic) plus the
 * manual layer (paymentStatus, billingCycle, amountBeforeVat).
 */

/** Firestore Timestamp / Date / ISO string → Date (null when unparseable) */
const toDateSafe = (value) => {
  if (!value) return null;
  const d =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
        ? value
        : typeof value?.seconds === "number"
          ? new Date(value.seconds * 1000)
          : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDate = (dateString) => {
  const date = toDateSafe(dateString);
  if (!date) return "—";
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const getRelativeDate = (dateStr) => {
  const date = toDateSafe(dateStr);
  if (!date) return "—";
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "היום";
  if (diffDays === 1) return "אתמול";
  if (diffDays < 7) return `לפני ${diffDays} ימים`;
  if (diffDays < 30) return `לפני ${Math.floor(diffDays / 7)} שבועות`;
  if (diffDays < 365) return `לפני ${Math.floor(diffDays / 30)} חודשים`;
  return formatDate(dateStr);
};

export const getActivityStatus = (dateStr) => {
  if (!dateStr) return { color: "#9ca3af", bg: "#f3f4f6", label: "לא ידוע", level: "unknown" };
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffHours / 24;
  if (diffHours < 24) return { color: "#16a34a", bg: "#f0fdf4", label: "פעיל היום", level: "active" };
  if (diffDays < 3) return { color: "#2563eb", bg: "#eff6ff", label: "פעיל לאחרונה", level: "recent" };
  if (diffDays < 7) return { color: "#ca8a04", bg: "#fef9c3", label: "לפני שבוע", level: "week" };
  if (diffDays < 14) return { color: "#ea580c", bg: "#fff7ed", label: "לא פעיל", level: "inactive" };
  return { color: "#ef4444", bg: "#fef2f2", label: "נעלם", level: "gone" };
};

export const getTrialDaysLeft = (trialEndDate) => {
  if (!trialEndDate) return null;
  const end = new Date(trialEndDate);
  const now = new Date();
  return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
};

export const BILLING_CYCLE_LABELS = {
  monthly: "חודשי",
  one_time: "תשלום חד-פעמי",
};

/** Manual payment status (niv_clinic_crm.paymentStatus) — Oct 2026 model. */
export const PAYMENT_STATUS_ORDER = ["not_paying", "paying", "left"];
export const PAYMENT_STATUS_LABELS = {
  not_paying: "לא משלם",
  paying: "משלם",
  left: "עזב",
};

export const isPayingRow = (row) => row?.crm?.paymentStatus === "paying";

/** Israeli VAT rate — single configurable source of truth for the NiV dashboard. */
export const VAT_RATE = 0.18;
export const VAT_PERCENT_LABEL = `מע"מ ${Math.round(VAT_RATE * 100)}%`;

export const addVat = (amount) => (Number(amount) || 0) * (1 + VAT_RATE);
export const removeVat = (amount) => (Number(amount) || 0) / (1 + VAT_RATE);

/** Pre-VAT deal amount of a CRM record (amountBeforeVat canonical, legacy fallback). */
export const crmAmountPreVat = (crm) => {
  if (!crm) return 0;
  if (crm.amountBeforeVat != null) return Number(crm.amountBeforeVat) || 0;
  const amount = Number(crm.amount) || 0;
  if (!amount) return 0;
  return crm.vatIncluded === false ? amount : removeVat(amount);
};

/** "YYYY-MM-DD" | ISO → {year, month} or null */
const toYm = (value) => {
  const d = toDateSafe(value);
  if (!d) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

/**
 * Months a one-time deal covers: from oneTimeStartDate (fallback today) to
 * planEndDate ("פעיל עד"), calendar months, ceil, min 1. Mirrors
 * functions/telemetryScoringHelper.js oneTimeMonths — keep both in sync.
 */
export const getOneTimeMonths = (crm, planEndDate, now = new Date()) => {
  const start = toDateSafe(crm?.oneTimeStartDate) || now;
  const end = toDateSafe(planEndDate);
  if (!end) return 1;
  const a = toYm(start);
  const b = toYm(end);
  let months = (b.year - a.year) * 12 + (b.month - a.month);
  if (end.getDate() > start.getDate()) months += 1;
  return Math.max(1, months);
};

/**
 * Monthly-normalized income BEFORE VAT for a clinic (MRR contribution).
 * monthly payer → amountBeforeVat; one-time payer → amountBeforeVat spread
 * over the deal window (oneTimeStartDate → planEndDate); otherwise 0.
 */
export const normalizeMonthlyAmount = (crm, planEndDate = null, now = new Date()) => {
  if (!crm || crm.paymentStatus !== "paying") return 0;
  const amount = crmAmountPreVat(crm);
  if (!amount) return 0;
  if (crm.billingCycle === "one_time") return amount / getOneTimeMonths(crm, planEndDate, now);
  if (crm.billingCycle === "monthly") return amount;
  return 0;
};

export const sumMonthlyIncome = (rows) =>
  rows.reduce((sum, row) => sum + normalizeMonthlyAmount(row.crm, row.planEndDate), 0);

export const formatShekels = (amount) => {
  if (!amount && amount !== 0) return "—";
  return `₪${Math.round(amount).toLocaleString("he-IL")}`;
};

/** planEndDate helpers — "בתשלום עד" is read-only real data */
export const getPlanEndStatus = (planEndDate) => {
  if (!planEndDate) return null;
  const end = new Date(planEndDate);
  const daysLeft = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return { level: "expired", daysLeft, label: "פג תוקף" };
  if (daysLeft <= 14) return { level: "soon", daysLeft, label: `מסתיים בעוד ${daysLeft} ימים` };
  return { level: "ok", daysLeft, label: formatDate(planEndDate) };
};

export const PLAN_END_TOOLTIP =
  "תאריך סיום המסלול של הקליניקה — נערך בפרטי הקליניקה. בתאריך זה המסלול יורד אוטומטית לניסיון שפג.";

/** Countdown window: clinics enter the morning payments queue this many days
 * before their planEndDate and stay there daily (7, 6, 5…) until extended. */
export const PLAN_END_COUNTDOWN_DAYS = 7;

/**
 * Payments queue for the morning briefing.
 * Countdown model: ANY clinic with a planEndDate appears daily during the
 * last PLAN_END_COUNTDOWN_DAYS days and once expired — the admin extends the
 * date manually (usually while logging a payment). Paying clinics without a
 * planEndDate are flagged as "missing".
 * Returns [{row, kind: "expired"|"soon"|"missing", label, daysLeft, expectedAmount, cycleLabel}]
 */
export const buildPaymentsQueue = (rows) => {
  const queue = [];
  rows.forEach((row) => {
    const paying = isPayingRow(row);
    const expectedAmount = paying ? crmAmountPreVat(row.crm) || null : null;
    const cycleLabel = row.crm?.billingCycle
      ? BILLING_CYCLE_LABELS[row.crm.billingCycle] || null
      : null;
    if (!row.planEndDate) {
      // Only one-time deals need an end date; monthly payers (הו"ק or not)
      // never have one.
      if (paying && row.crm?.billingCycle === "one_time") {
        queue.push({
          row,
          kind: "missing",
          label: "משלם ללא תאריך סיום",
          daysLeft: null,
          expectedAmount,
          cycleLabel,
        });
      }
      return;
    }
    const end = new Date(row.planEndDate);
    const daysLeft = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) {
      queue.push({ row, kind: "expired", label: "פג תוקף", daysLeft, expectedAmount, cycleLabel });
    } else if (daysLeft <= PLAN_END_COUNTDOWN_DAYS) {
      queue.push({
        row,
        kind: "soon",
        label: daysLeft === 0 ? "מסתיים היום" : `מסתיים בעוד ${daysLeft} ימים`,
        daysLeft,
        expectedAmount,
        cycleLabel,
      });
    }
  });
  // Expired first, then soon (nearest first), then missing
  const rank = { expired: 0, soon: 1, missing: 2 };
  queue.sort((a, b) => rank[a.kind] - rank[b.kind] || (a.daysLeft ?? 99) - (b.daysLeft ?? 99));
  return queue;
};

// ============================================
// STANDING ORDER (הו"ק באשראי) — monthly verification queue
// ============================================

export const PAYMENT_METHOD_LABELS = {
  standing_order: 'הו"ק',
  transfer: "העברה",
  credit: "אשראי",
  other: "אחר",
};

const HEBREW_MONTHS = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

/** "YYYY-MM" → "אוקטובר 2026" */
export const formatMonthKey = (monthKey) => {
  const m = /^(\d{4})-(\d{2})$/.exec(monthKey || "");
  if (!m) return monthKey || "—";
  const idx = Number(m[2]) - 1;
  return `${HEBREW_MONTHS[idx] || m[2]} ${m[1]}`;
};

/** Today's {year, month(1-12), day} on the Asia/Jerusalem wall clock. */
export const getIsraelYmd = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
};

export const toMonthKey = (year, month) => `${year}-${String(month).padStart(2, "0")}`;

/** Current Israel month as "YYYY-MM" (used when enabling a standing order). */
export const getCurrentIsraelMonthKey = (now = new Date()) => {
  const { year, month } = getIsraelYmd(now);
  return toMonthKey(year, month);
};

/** Max months of catch-up the queue will surface for one clinic. */
export const STANDING_ORDER_MAX_MONTHS = 12;

/**
 * Standing-order verification queue for the morning briefing.
 * For every paying clinic with isStandingOrder + chargeDay, walk the months
 * from standingOrderSince (capped at STANDING_ORDER_MAX_MONTHS back) up to the
 * current Israel month. A month is DUE once today >= chargeDay of that month,
 * and PENDING while there is no payments[] entry with that month and the month
 * was not skipped. One queue entry per pending month, oldest first.
 *
 * Returns [{ key, row, month, label, chargeDate, expectedAmount, failedAt }]
 */
export const buildStandingOrderQueue = (rows, now = new Date()) => {
  const today = getIsraelYmd(now);
  const queue = [];
  (rows || []).forEach((row) => {
    const crm = row?.crm;
    if (crm?.paymentStatus !== "paying" || !crm?.isStandingOrder) return;
    const chargeDay = Number(crm.chargeDay);
    if (!Number.isInteger(chargeDay) || chargeDay < 1 || chargeDay > 28) return;

    // Starting month: standingOrderSince, else current month
    const sinceMatch = /^(\d{4})-(\d{2})$/.exec(crm.standingOrderSince || "");
    let y = sinceMatch ? Number(sinceMatch[1]) : today.year;
    let m = sinceMatch ? Number(sinceMatch[2]) : today.month;

    // Cap catch-up window
    const monthsBack = (today.year - y) * 12 + (today.month - m);
    if (monthsBack > STANDING_ORDER_MAX_MONTHS) {
      const total = today.year * 12 + (today.month - 1) - STANDING_ORDER_MAX_MONTHS;
      y = Math.floor(total / 12);
      m = (total % 12) + 1;
    }

    const paidMonths = new Set(
      (crm.payments || []).map((p) => p.month).filter(Boolean)
    );
    const skipped = new Set(crm.skippedMonths || []);
    const failures = crm.standingOrderFailures || {};
    const expectedAmount = crmAmountPreVat(crm) || null;

    while (y < today.year || (y === today.year && m <= today.month)) {
      const key = toMonthKey(y, m);
      const isDue = y < today.year || m < today.month || today.day >= chargeDay;
      if (isDue && !paidMonths.has(key) && !skipped.has(key)) {
        queue.push({
          key: `${row.clinicId}_${key}`,
          row,
          month: key,
          label: formatMonthKey(key),
          chargeDate: `${key}-${String(chargeDay).padStart(2, "0")}`,
          expectedAmount,
          expectedAmountBeforeVat: expectedAmount,
          failedAt: failures[key] || null,
        });
      }
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
  });
  queue.sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
  return queue;
};

/**
 * Seat mismatch for a paying clinic: more active therapists than seats paid
 * for. Returns {active, paid, excess} or null (null paidTherapistsCount =
 * not configured → never alert).
 */
export const getSeatMismatch = (row) => {
  if (!isPayingRow(row)) return null;
  const paid = row.crm.paidTherapistsCount;
  if (paid == null) return null;
  const active = row.activeTherapistsCount ?? row.therapistsCount ?? 0;
  return active > paid ? { active, paid, excess: active - paid } : null;
};

export const buildSeatMismatchQueue = (rows) =>
  rows
    .map((row) => ({ row, mismatch: getSeatMismatch(row) }))
    .filter((x) => x.mismatch)
    .sort((a, b) => b.mismatch.excess - a.mismatch.excess);

/** Days after approval before an unregistered prospect is surfaced. */
export const STALE_APPROVED_DAYS = 7;

/**
 * Approved-but-never-registered prospects that have been waiting too long.
 * Returns [{prospect, daysWaiting}], oldest first.
 */
export const buildStaleApprovedQueue = (prospects, staleDays = STALE_APPROVED_DAYS) => {
  const now = Date.now();
  const dayMs = 1000 * 60 * 60 * 24;
  return (prospects || [])
    .filter((prospect) => prospect.stage === "approved" && !prospect.staleApprovedDismissedAt)
    .map((prospect) => {
      const since = prospect.whitelistedAt || prospect.updatedAt || prospect.createdAt;
      if (!since) return null;
      const daysWaiting = Math.floor((now - new Date(since).getTime()) / dayMs);
      return { prospect, daysWaiting };
    })
    .filter((x) => x && x.daysWaiting >= staleDays)
    .sort((a, b) => b.daysWaiting - a.daysWaiting);
};

/**
 * Suggested planEndDate after logging a payment: extend from the later of
 * (current planEndDate, payment date) by one month — extend, never shorten.
 * Returns "YYYY-MM-DD". (Third arg kept for call-site compatibility.)
 */
export const suggestPlanEndAfterPayment = (currentPlanEndIso, paymentDateIso) => {
  const months = 1;
  const payment = paymentDateIso ? new Date(paymentDateIso) : new Date();
  const current = currentPlanEndIso ? new Date(currentPlanEndIso) : null;
  const base = current && current > payment ? current : payment;
  const suggested = new Date(base);
  const day = suggested.getDate();
  suggested.setMonth(suggested.getMonth() + months);
  // Month-length overflow (e.g. Jan 31 + 1M) — clamp to end of target month
  if (suggested.getDate() !== day) suggested.setDate(0);
  const y = suggested.getFullYear();
  const m = String(suggested.getMonth() + 1).padStart(2, "0");
  const d = String(suggested.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Tag descriptors for a clinics-overview row. Rendering happens in ClinicTags.
 * Each tag: { key, label, tone: "plan"|"paying"|"notPaying"|"solo"|"multi"|"trial"|"trialDanger"|"planEnd"|"planEndDanger"|activity level }
 */
export const buildClinicTags = (row) => {
  const tags = [];
  // Plan (real)
  tags.push({ key: "plan", label: row.planName || row.plan, tone: row.plan === "trial" ? "planTrial" : "plan" });
  // Payment status (manual)
  const status = row.crm?.paymentStatus || "not_paying";
  tags.push({
    key: "paying",
    label: PAYMENT_STATUS_LABELS[status] || PAYMENT_STATUS_LABELS.not_paying,
    tone: status === "paying" ? "paying" : status === "left" ? "left" : "notPaying",
  });
  // Solo / multi (real)
  tags.push(
    row.isSoloClinic
      ? { key: "solo", label: "סולו", tone: "solo" }
      : { key: "multi", label: "מרובת מטפלים", tone: "multi" }
  );
  // Trial days left (real)
  if (row.plan === "trial") {
    const daysLeft = getTrialDaysLeft(row.trialEndDate);
    if (daysLeft !== null) {
      tags.push({
        key: "trialDays",
        label: daysLeft < 0 ? "ניסיון פג" : `${daysLeft} ימי ניסיון`,
        tone: daysLeft <= 3 ? "trialDanger" : "trial",
      });
    }
  }
  // Active-until (real planEndDate, read-only)
  if (row.planEndDate) {
    const endStatus = getPlanEndStatus(row.planEndDate);
    tags.push({
      key: "planEnd",
      label: `פעיל עד ${formatDate(row.planEndDate)}`,
      tone: endStatus.level === "expired" ? "planEndDanger" : "planEnd",
    });
  }
  // Churned: reverted from a paid plan by the cron and not re-upgraded
  if (row.plan === "trial" && row.previousPaidPlan) {
    tags.push({ key: "churned", label: "נטש — היה משלם", tone: "churned" });
  }
  // Seat mismatch: more active therapists than paid seats
  const seatMismatch = getSeatMismatch(row);
  if (seatMismatch) {
    tags.push({
      key: "seats",
      label: `${seatMismatch.active}/${seatMismatch.paid} מושבים`,
      tone: "seatsDanger",
    });
  }
  return tags;
};

// ============================================
// CLINICS TAB v2 — activation funnel, contact tracking, segments, trends
// ============================================

/** Funnel steps (in order). `done` per step is true | false | null (pending). */
export const ACTIVATION_STEPS = [
  { key: "registered", label: "נרשמה" },
  { key: "onboarded", label: "סיימה הדרכה" },
  { key: "firstPatient", label: "מטופל ראשון" },
  { key: "firstAppointment", label: "פגישה ראשונה" },
  { key: "firstCompleted", label: "פגישה שהושלמה" },
];

/**
 * @param row overview row
 * @param activation { appointments, completed } | undefined (not loaded) — null counts = unknown
 */
export const buildActivationSteps = (row, activation) => {
  const hasPatients = (row?.totalPatients || 0) > 0;
  // A clinic with no patients cannot have appointments — resolve without a count() call
  const appts = !hasPatients ? 0 : activation?.appointments ?? null;
  const completed = !hasPatients ? 0 : activation?.completed ?? null;
  const done = {
    registered: true,
    onboarded: row?.ownerOnboardingCompleted === true,
    firstPatient: hasPatients,
    firstAppointment: appts === null ? null : appts > 0,
    firstCompleted: completed === null ? null : completed > 0,
  };
  const steps = ACTIVATION_STEPS.map((s) => ({ ...s, done: done[s.key] }));
  const completedCount = steps.filter((s) => s.done === true).length;
  const pending = steps.some((s) => s.done === null);
  return { steps, completed: completedCount, pending };
};

/** Header funnel: how many clinics reached each step. */
export const buildActivationFunnel = (rows, activationMap = {}) => {
  const counts = ACTIVATION_STEPS.map((s) => ({ ...s, count: 0 }));
  rows.forEach((row) => {
    const { steps } = buildActivationSteps(row, activationMap[row.clinicId]);
    steps.forEach((s, i) => {
      if (s.done === true) counts[i].count++;
    });
  });
  return counts;
};

export const NEW_CLINIC_DAYS = 14;
export const TRIAL_ENDING_DAYS = 3;
export const CONTACT_RECENT_DAYS = 14;

const DAY_MS = 1000 * 60 * 60 * 24;
const daysSince = (iso, now = Date.now()) =>
  iso ? Math.floor((now - new Date(iso).getTime()) / DAY_MS) : null;

export const isNewClinic = (row, now = Date.now()) => {
  const age = daysSince(row?.createdAt, now);
  return age !== null && age <= NEW_CLINIC_DAYS;
};

export const isChurnedClinic = (row) => row?.plan === "trial" && !!row?.previousPaidPlan;

export const CONTACT_CHANNEL_LABELS = {
  call: "שיחה",
  whatsapp: "וואטסאפ",
  sms: "SMS",
  email: "אימייל",
  other: "אחר",
};

export const NEEDS_CONTACT_LABELS = {
  trialEnding: "הניסיון מסתיים — כדאי לדבר",
  churned: "נטשה — היה משלם",
};

/**
 * "Needs contact" trigger for a row, or null. Suppressed when the admin
 * dismissed this exact trigger (crm.contactDismissedKey === dismissKey);
 * a new anchor (new trial end, new revert) re-surfaces the clinic.
 * Precedence: churned > trialEnding. (The "new — never contacted" trigger
 * was removed Oct 2026: it only produced noise in the clinics list.)
 * Feeds the "דורשות קשר" KPI tile; the list itself no longer surfaces it.
 */
export const getNeedsContact = (row, now = Date.now()) => {
  if (!row || row.inactiveByAdmin) return null;
  const crm = row.crm || {};
  const last = crm.lastContactAt ? new Date(crm.lastContactAt).getTime() : null;
  const sinceContact = last !== null ? Math.floor((now - last) / DAY_MS) : null;
  let hit = null;

  if (isChurnedClinic(row)) {
    const revertedAt = row.planRevertedAt ? new Date(row.planRevertedAt).getTime() : 0;
    if (last === null || last < revertedAt) {
      hit = { reason: "churned", dismissKey: `churned:${row.planRevertedAt || "na"}` };
    }
  }
  if (!hit && row.plan === "trial" && row.trialEndDate) {
    const daysLeft = getTrialDaysLeft(row.trialEndDate);
    if (daysLeft !== null && daysLeft >= -7 && daysLeft <= TRIAL_ENDING_DAYS) {
      if (sinceContact === null || sinceContact > CONTACT_RECENT_DAYS) {
        hit = { reason: "trialEnding", dismissKey: `trialEnding:${row.trialEndDate}` };
      }
    }
  }
  if (!hit) return null;
  if (crm.contactDismissedKey === hit.dismissKey) return null;
  return { ...hit, label: NEEDS_CONTACT_LABELS[hit.reason] };
};

/** Last-contact chip data. */
export const getContactStatus = (row, now = Date.now()) => {
  const crm = row?.crm || {};
  if (!crm.lastContactAt) return { lastContactAt: null, daysAgo: null, channel: null, tone: "none" };
  const daysAgo = daysSince(crm.lastContactAt, now);
  const contacts = [...(crm.contacts || [])].sort(
    (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
  );
  const channel = contacts[0]?.channel || null;
  const tone = daysAgo <= CONTACT_RECENT_DAYS ? "fresh" : daysAgo <= 45 ? "aging" : "stale";
  return { lastContactAt: crm.lastContactAt, daysAgo, channel, tone };
};

export const isRiskRow = (row) => {
  if (!row) return false;
  if (row.plan === "trial") {
    const daysLeft = getTrialDaysLeft(row.trialEndDate);
    if (daysLeft !== null && daysLeft <= TRIAL_ENDING_DAYS) return true;
  }
  if (getActivityStatus(row.updatedAt).level === "gone") return true;
  if (isChurnedClinic(row)) return true;
  if (getSeatMismatch(row)) return true;
  if (getPaymentAlert(row)?.kind === "expired") return true;
  return false;
};

/** Segment tabs. Non-inactive segments exclude admin-deactivated clinics. */
export const SEGMENTS = [
  { id: "all", label: "הכל", predicate: (r) => !r.inactiveByAdmin },
  { id: "new", label: "חדשות", predicate: (r) => !r.inactiveByAdmin && isNewClinic(r) },
  { id: "trial", label: "בניסיון", predicate: (r) => !r.inactiveByAdmin && r.plan === "trial" },
  { id: "paying", label: "משלמות", predicate: (r) => !r.inactiveByAdmin && isPayingRow(r) },
  { id: "risk", label: "בסיכון", predicate: (r) => !r.inactiveByAdmin && isRiskRow(r) },
  { id: "left", label: "עזבו", predicate: (r) => !r.inactiveByAdmin && r.crm?.paymentStatus === "left" },
  { id: "inactive", label: "לא פעילות", predicate: (r) => !!r.inactiveByAdmin },
];

/** Row tint precedence: inactive > left > risk > trial > paying > new > neutral */
export const getRowStatus = (row) => {
  if (!row) return "neutral";
  if (row.inactiveByAdmin) return "inactive";
  if (row.crm?.paymentStatus === "left") return "left";
  if (isRiskRow(row)) return "risk";
  if (row.plan === "trial") return "trial";
  if (isPayingRow(row)) return "paying";
  if (isNewClinic(row)) return "new";
  return "neutral";
};

// ============================================
// CLINICS LIST (Oct 2026 compact table) — payment / next-payment / revenue
// ============================================

/** Pre-VAT value of a VAT-inclusive-or-not amount under a CRM record's flag. */
const preVatOf = (amount, crm, explicit) => {
  if (explicit != null) return Number(explicit) || 0;
  const n = Number(amount) || 0;
  if (!n) return 0;
  return crm?.vatIncluded === false ? n : removeVat(n);
};

/**
 * Payment summary for the list's payment cell / detail banners.
 * { status, statusLabel, amountPreVat, cycle, cycleLabel, isStandingOrder,
 *   activeUntil (one_time only), monthlyEquivalent }
 */
export const getPayingSummary = (crm, planEndDate = null) => {
  const status = crm?.paymentStatus || "not_paying";
  const cycle = status === "paying" ? crm?.billingCycle || null : null;
  return {
    status,
    statusLabel: PAYMENT_STATUS_LABELS[status] || PAYMENT_STATUS_LABELS.not_paying,
    amountPreVat: status === "paying" ? crmAmountPreVat(crm) : 0,
    cycle,
    cycleLabel: cycle ? BILLING_CYCLE_LABELS[cycle] || null : null,
    isStandingOrder: status === "paying" && !!crm?.isStandingOrder,
    activeUntil: cycle === "one_time" ? planEndDate || null : null,
    monthlyEquivalent: normalizeMonthlyAmount(crm, planEndDate),
  };
};

/**
 * Payment alert for a row: one-time payer whose "פעיל עד" passed while the
 * status is still "paying" (expired), or a one-time payer without a date
 * (missingDate). null otherwise.
 */
export const getPaymentAlert = (row, now = Date.now()) => {
  const crm = row?.crm;
  if (!crm || crm.paymentStatus !== "paying" || crm.billingCycle !== "one_time") return null;
  if (!row.planEndDate) {
    return { kind: "missingDate", label: 'תשלום חד-פעמי ללא תאריך "פעיל עד"' };
  }
  const end = toDateSafe(row.planEndDate);
  if (!end) return null;
  const daysOver = Math.floor((now - end.getTime()) / DAY_MS);
  if (daysOver < 0) return null;
  return {
    kind: "expired",
    daysOver,
    label: daysOver === 0 ? "התאריך עבר היום והסטטוס עדיין משלם" : `התאריך עבר לפני ${daysOver} ימים והסטטוס עדיין משלם`,
  };
};

/**
 * Status ↔ plan inconsistency hint (manual status never touches the plan;
 * the UI offers a one-click fix instead).
 */
export const getPaymentPlanHint = (row) => {
  const crm = row?.crm;
  if (!row || !crm) return null;
  const plan = row.plan || "trial";
  if (crm.paymentStatus === "left" && plan !== "trial") {
    return {
      kind: "revertToTrial",
      label: `הקליניקה סומנה כעזבה אך עדיין במסלול ${row.planName || plan}`,
      actionLabel: "הורד לניסיון",
    };
  }
  if (crm.paymentStatus === "paying" && plan === "trial") {
    return {
      kind: "setPaidPlan",
      label: "הקליניקה מסומנת כמשלמת אך עדיין במסלול ניסיון",
      actionLabel: "עדכן מסלול",
    };
  }
  if (crm.paymentStatus === "paying" && crm.billingCycle === "monthly" && row.planEndDate) {
    return {
      kind: "clearEndDate",
      label: `מנוי חודשי עם תאריך סיום (${formatDate(row.planEndDate)}) — בתאריך זה הקליניקה תרד לניסיון`,
      actionLabel: "נקה תאריך",
    };
  }
  return null;
};

/**
 * Next charge date for a standing-order clinic ("YYYY-MM-DD"): chargeDay of
 * the current Israel month if still ahead (or today), else next month.
 */
export const getNextStandingOrderDate = (crm, now = new Date()) => {
  const chargeDay = Number(crm?.chargeDay);
  if (!crm?.isStandingOrder || !Number.isInteger(chargeDay) || chargeDay < 1 || chargeDay > 28) {
    return null;
  }
  const { year, month, day } = getIsraelYmd(now);
  let y = year;
  let m = month;
  if (day > chargeDay) {
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return `${toMonthKey(y, m)}-${String(chargeDay).padStart(2, "0")}`;
};

/**
 * "Next payment" cell for a clinic row.
 * Standing order → next charge date (kind "charge"); one-time deal → the
 * real planEndDate (kind "planEnd" = "פעיל עד"). null otherwise (monthly
 * without הו"ק, not paying, left).
 * tone: "danger" (expired / pending months) | "soon" (≤7 days) | "ok".
 */
export const getNextPayment = (row, pendingStandingOrderMonths = 0, now = new Date()) => {
  const crm = row?.crm;
  if (!crm || crm.paymentStatus !== "paying") return null;
  if (crm.isStandingOrder) {
    const date = getNextStandingOrderDate(crm, now);
    if (!date) return null;
    const daysLeft = Math.ceil((new Date(date) - now) / DAY_MS);
    return {
      kind: "charge",
      date,
      daysLeft,
      pendingMonths: pendingStandingOrderMonths,
      tone: pendingStandingOrderMonths > 0 ? "danger" : daysLeft <= 7 ? "soon" : "ok",
    };
  }
  if (crm.billingCycle !== "one_time" || !row.planEndDate) return null;
  const status = getPlanEndStatus(row.planEndDate);
  return {
    kind: "planEnd",
    date: row.planEndDate,
    daysLeft: status.daysLeft,
    pendingMonths: 0,
    tone: status.level === "expired" ? "danger" : status.level === "soon" ? "soon" : "ok",
  };
};

/**
 * Lifetime revenue logged for a clinic (payments[] on the CRM record).
 * Returns { gross, preVat, count }. Legacy payments without amountBeforeVat
 * fall back to the record's VAT flag.
 */
export const sumClinicRevenue = (crm) => {
  const payments = crm?.payments || [];
  let gross = 0;
  let preVat = 0;
  payments.forEach((p) => {
    const amount = Number(p?.amount) || 0;
    gross += amount;
    preVat += preVatOf(amount, crm, p?.amountBeforeVat);
  });
  return { gross, preVat, count: payments.length };
};

/** New clinics per ISO week (Sunday-anchored), oldest → newest. */
export const buildWeeklySignups = (rows, weeks = 12, now = Date.now()) => {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - start.getDay()); // this week's Sunday
  const buckets = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() - i * 7);
    buckets.push({ weekStart: weekStart.toISOString(), count: 0 });
  }
  const firstMs = new Date(buckets[0].weekStart).getTime();
  rows.forEach((row) => {
    if (!row.createdAt) return;
    const t = new Date(row.createdAt).getTime();
    if (Number.isNaN(t) || t < firstMs) return;
    const idx = Math.min(weeks - 1, Math.floor((t - firstMs) / (7 * DAY_MS)));
    buckets[idx].count++;
  });
  return buckets;
};

/**
 * Incoming payments per calendar month (Israel time), oldest → newest, from
 * every clinic's manual payment log (crm.payments[]). Amounts are PRE-VAT
 * (same rule as sumClinicRevenue). Payments dated before the window are
 * dropped; the current month is the last bucket.
 * Returns [{ month: "YYYY-MM", total, count }]
 */
export const buildMonthlyPayments = (rows, months = 12, now = new Date()) => {
  const { year, month } = getIsraelYmd(now);
  const buckets = [];
  const index = {};
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(year, month - 1 - i, 1));
    const key = toMonthKey(d.getUTCFullYear(), d.getUTCMonth() + 1);
    index[key] = buckets.length;
    buckets.push({ month: key, total: 0, count: 0 });
  }
  rows.forEach((row) => {
    const crm = row?.crm;
    (crm?.payments || []).forEach((p) => {
      if (!p?.date) return;
      const d = new Date(p.date);
      if (Number.isNaN(d.getTime())) return;
      const ymd = getIsraelYmd(d);
      const key = toMonthKey(ymd.year, ymd.month);
      const idx = index[key];
      if (idx === undefined) return;
      const amount = Number(p.amount) || 0;
      buckets[idx].total += preVatOf(amount, crm, p.amountBeforeVat);
      buckets[idx].count += 1;
    });
  });
  return buckets;
};

/**
 * Inactivity tone from a "days since last login" count. Thresholds mirror the
 * niv_alerts health cards (7 / 30 days) so the two surfaces never disagree.
 */
export const loginTone = (days) => {
  if (days === null || days === undefined) return "low";
  const n = Number(days);
  if (!Number.isFinite(n)) return "none";
  if (n >= 30) return "low";
  if (n >= 7) return "mid";
  return "high";
};

/** "לפני X ימים" from a day count. */
export const relativeDaysLabel = (days) => {
  if (days === null || days === undefined) return "מעולם";
  const n = Number(days);
  if (!Number.isFinite(n)) return "—";
  if (n <= 0) return "היום";
  if (n === 1) return "אתמול";
  return `לפני ${n} ימים`;
};

/**
 * "Not really using the system": no login in the last 30 days, or nothing at
 * all happened in 30 days (no meetings and no sessions).
 */
export const isNoUsage = (usage) => {
  if (!usage) return true;
  const days = usage.logins?.daysSinceLastLogin;
  if (days == null || Number(days) > 30) return true;
  return (
    (usage.meetings?.past30d?.total || 0) === 0 &&
    (usage.logins?.sessions30d || 0) === 0
  );
};

// ============================================
// OVERVIEW STATS — hero cards shared by the morning
// briefing and the clinics tab (NivOverviewStats)
// ============================================

/**
 * Full stats bundle for the overview hero cards.
 * @param rows active overview rows (admin-deactivated already filtered out)
 * @param inactiveCount how many rows were filtered out as inactiveByAdmin
 */
export const buildOverviewStats = (rows = [], inactiveCount = 0) => {
  const total = rows.length;
  const paying = rows.filter(isPayingRow).length;
  const left = rows.filter((r) => r.crm?.paymentStatus === "left").length;
  const trial = rows.filter((r) => r.plan === "trial" && !r.previousPaidPlan).length;
  const churned = rows.filter(isChurnedClinic).length;
  const free = Math.max(0, total - paying - trial - churned);

  // Money — MRR before VAT: monthly payers' amount + one-time deals spread
  // over their active window. Everything the dashboard shows is pre-VAT.
  let netMonthly = 0;
  let netMonthlyFromMonthly = 0;
  let netMonthlyFromOneTime = 0;
  let oneTimeActiveSum = 0;
  rows.forEach((row) => {
    const net = normalizeMonthlyAmount(row.crm, row.planEndDate);
    if (!net) return;
    netMonthly += net;
    if (row.crm?.billingCycle === "one_time") {
      netMonthlyFromOneTime += net;
      oneTimeActiveSum += crmAmountPreVat(row.crm);
    } else {
      netMonthlyFromMonthly += net;
    }
  });
  const oneTimePayers = rows.filter((r) => isPayingRow(r) && r.crm?.billingCycle === "one_time").length;
  const monthlyPayers = Math.max(0, paying - oneTimePayers);

  // Activity — derived from clinic.updatedAt buckets
  const activity = { active: 0, recent: 0, week: 0, inactive: 0, gone: 0, unknown: 0 };
  rows.forEach((row) => {
    const level = getActivityStatus(row.updatedAt).level;
    activity[level] = (activity[level] || 0) + 1;
  });

  const activePatients = rows.reduce((sum, r) => sum + (r.activePatients || 0), 0);
  const totalPatients = rows.reduce((sum, r) => sum + (r.totalPatients || 0), 0);
  const therapists = rows.reduce((sum, r) => sum + (r.activeTherapistsCount || 0), 0);
  const solo = rows.filter((r) => r.isSoloClinic).length;

  return {
    total,
    inactiveByAdmin: inactiveCount,
    paying,
    left,
    trial,
    churned,
    free,
    conversionRate: total ? Math.round((paying / total) * 100) : 0,
    money: {
      netMonthly,
      netYearly: netMonthly * 12,
      fromMonthly: netMonthlyFromMonthly,
      fromOneTime: netMonthlyFromOneTime,
      oneTimeActiveSum,
      monthlyPayers,
      oneTimePayers,
      arpa: paying ? netMonthly / paying : 0,
    },
    activity,
    activePatients,
    totalPatients,
    therapists,
    solo,
    multi: Math.max(0, total - solo),
    avgPatients: total ? Math.round(activePatients / total) : 0,
    needsContact: rows.filter((r) => !!getNeedsContact(r)).length,
    paymentsDue: buildPaymentsQueue(rows).length,
    seatMismatches: buildSeatMismatchQueue(rows).length,
    trialEndingSoon: rows.filter((r) => {
      if (r.plan !== "trial") return false;
      const left = getTrialDaysLeft(r.trialEndDate);
      return left !== null && left <= 3 && left >= -7;
    }).length,
    newThisMonth: rows.filter((r) => {
      if (!r.createdAt) return false;
      const d = new Date(r.createdAt);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length,
  };
};

// ---------------------------------------------------------------------------
// /start lead extras (read-side; see DEVELOPER_GUIDE §28.2)
// ---------------------------------------------------------------------------

const UTM_DISPLAY_KEYS = ["source", "medium", "campaign", "term", "content", "gclid", "fbclid"];

/**
 * Pick `profession`, the latest `message` and `utm` from the newest call
 * request that has them. Requests may arrive in any order; we sort by
 * createdAt desc. Returns nulls when nothing is known.
 */
export const pickLeadExtras = (callRequests = []) => {
  const toMs = (v) => {
    if (!v) return 0;
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (v?.seconds) return v.seconds * 1000;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : 0;
  };
  const sorted = [...(callRequests || [])].sort((a, b) => toMs(b?.createdAt) - toMs(a?.createdAt));
  const firstWith = (pred) => sorted.find(pred);
  const profReq = firstWith((r) => typeof r?.profession === "string" && r.profession.trim());
  const msgReq = firstWith((r) => typeof r?.message === "string" && r.message.trim());
  const utmReq = firstWith((r) => r?.utm && typeof r.utm === "object" && Object.keys(r.utm).length > 0);
  return {
    profession: profReq ? profReq.profession.trim() : null,
    message: msgReq ? msgReq.message.trim() : null,
    utm: utmReq ? utmReq.utm : null,
  };
};

/** "source / medium / campaign" one-liner for badges; null when empty. */
export const formatUtmSummary = (utm) => {
  if (!utm || typeof utm !== "object") return null;
  const parts = UTM_DISPLAY_KEYS.filter((k) => utm[k]).map((k) => (k === "gclid" || k === "fbclid" ? k : utm[k]));
  return parts.length > 0 ? parts.join(" / ") : null;
};
